import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Gauge, TrendingUp, Activity, Play, Zap, AlertTriangle, X, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import { trackEvent } from "@/lib/analytics";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Terms and Conditions Content
const TERMS_AND_CONDITIONS = `
PIVOT PERFORMANCE - TERMS AND CONDITIONS

Last Updated: March 2026

1. ACCEPTANCE OF TERMS

By registering for an account or using the Pivot Performance mobile application, you ("User") agree to be bound by these Terms and Conditions. If you do not agree, do not use the application.

2. HEALTH & SAFETY WARNING (CRITICAL)

⚠️ PIVOT PERFORMANCE IS NOT A MEDICAL ORGANIZATION.

• CONSULT A PHYSICIAN: You should consult with a healthcare professional before starting the "Break the Cycle" program or any high-intensity conditioning.

• ASSUMPTION OF RISK: You acknowledge that basketball conditioning involves strenuous physical activity. Risks include, but are not limited to, muscle strains, joint injuries, heart attack, or even death.

• USER RESPONSIBILITY: You are the best judge of your own physical limits. If you feel pain, dizziness, or shortness of breath, STOP IMMEDIATELY.

3. RELEASE OF LIABILITY (CALIFORNIA CIVIL CODE)

In consideration of being allowed to use the App, you hereby release [Your LLC Name], DBA Pivot Performance, its owners, and employees from any and all claims for ordinary negligence arising out of your use of the App.

WAIVER OF SECTION 1542: You expressly waive the protections of California Civil Code Section 1542, which states:

"A general release does not extend to claims that the creditor or releasing party does not know or suspect to exist in his or her favor at the time of executing the release and that, if known by him or her, would have materially affected his or her settlement with the debtor or releasing party."

4. PROGRAM FEATURES

• Features and content may be added, improved, or removed at any time.
• User feedback may be used to improve the application.
• Access may be revoked for violation of these Terms.

5. USER DATA

• Your email address and registration information will be stored securely.
• Progress data, workout history, and photos are stored for your benefit.
• We do not sell your personal information to third parties.

6. INTELLECTUAL PROPERTY

All content, including workout programs, drill descriptions, and app design, is the property of Pivot Performance and may not be reproduced without permission.

7. MODIFICATIONS

We reserve the right to modify these terms at any time. Continued use of the application constitutes acceptance of modified terms.

8. GOVERNING LAW

These Terms shall be governed by the laws of the State of California.

BY CLICKING "I ACCEPT" OR REGISTERING FOR AN ACCOUNT, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
`;

const LoginPage = ({ onLogin }) => {
  // Read ?mode=register from URL to start in register tab
  const initialIsLogin = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('mode') !== 'register'
    : true;
  const sessionExpired = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('reason') === 'expired';
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [betaStatus, setBetaStatus] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    date_of_birth: ""
  });
  const { t } = useLanguage();

  // Max DOB = today minus 13 years (HTML5 date input enforces this client-side)
  const maxDob = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 13);
    return d.toISOString().split('T')[0];
  })();

  // Min DOB = today minus 120 years
  const minDob = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 120);
    return d.toISOString().split('T')[0];
  })();

  useEffect(() => {
    fetchBetaStatus();
  }, []);

  const fetchBetaStatus = async () => {
    try {
      const response = await axios.get(`${API}/beta/status`);
      setBetaStatus(response.data);
    } catch (error) {
      console.error('Error fetching beta status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require terms acceptance for registration
    if (!isLogin && !acceptedTerms) {
      toast.error("You must accept the Terms and Conditions to register");
      return;
    }

    // COPPA 13+ age gate (client-side check; backend re-validates)
    if (!isLogin) {
      if (!formData.date_of_birth) {
        toast.error("Please enter your date of birth");
        return;
      }
      const dob = new Date(formData.date_of_birth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 13) {
        toast.error("You must be at least 13 years old to use Pivot Performance.");
        return;
      }
      if (dob > today) {
        toast.error("Date of birth cannot be in the future");
        return;
      }
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            date_of_birth: formData.date_of_birth,
            accepted_terms: true,
            terms_accepted_date: new Date().toISOString()
          };
      
      const response = await axios.post(endpoint, payload);
      const { token, user } = response.data;
      
      trackEvent(isLogin ? 'login' : 'sign_up', { method: 'email' });
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      onLogin(token, user);
      // If we got here from an expired-session redirect, bounce the user back to the page they came from.
      try {
        const back = new URLSearchParams(window.location.search).get('back');
        if (back && back.startsWith('/')) {
          window.location.href = back;
        }
      } catch { /* noop */ }
    } catch (error) {
      trackEvent(isLogin ? 'login_failed' : 'sign_up_failed', {
        reason: error.response?.status || 'network_error'
      });
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const features = [
    {
      icon: Gauge,
      title: t('engine_monitor'),
      description: t('engine_desc')
    },
    {
      icon: TrendingUp,
      title: t('vertical_tracker'),
      description: t('vertical_desc')
    },
    {
      icon: Activity,
      title: t('ai_coaching'),
      description: t('ai_desc')
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-30">
        <LanguageSelector />
      </div>
      
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/1080882/pexels-photo-1080882.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.4) grayscale(30%)'
        }}
      />
      
      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80 z-[1]" />

      {/* Terms and Conditions Modal */}
      <AnimatePresence>
        {showTermsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowTermsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-zinc-800">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  <h2 className="text-lg font-bold text-white">Terms and Conditions</h2>
                </div>
                <button 
                  onClick={() => setShowTermsModal(false)}
                  className="p-1 hover:bg-zinc-700 rounded transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {/* Modal Content */}
              <ScrollArea className="h-[60vh] p-6">
                <div className="prose prose-invert prose-sm max-w-none">
                  {/* Health Warning Banner */}
                  <div className="bg-red-900/30 border border-red-600 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-red-400 font-bold text-base m-0 mb-2">⚠️ HEALTH & SAFETY WARNING</h3>
                        <p className="text-red-200 text-sm m-0">
                          Pivot Performance is NOT a medical organization. Consult a healthcare professional before starting any high-intensity conditioning program. Basketball conditioning involves risks including muscle strains, joint injuries, heart attack, or even death.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <pre className="whitespace-pre-wrap text-gray-300 text-sm font-sans leading-relaxed">
                    {TERMS_AND_CONDITIONS}
                  </pre>
                </div>
              </ScrollArea>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-zinc-700 bg-zinc-800">
                <Button
                  onClick={() => {
                    setAcceptedTerms(true);
                    setShowTermsModal(false);
                    toast.success("Terms accepted");
                  }}
                  className="w-full bg-red-600 hover:bg-red-500 text-white font-bold"
                >
                  I Accept These Terms
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl mx-auto">
          
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <motion.img
              src="/logo-seal.png"
              alt="Pivot Performance Lab"
              className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-6 rounded-full shadow-2xl shadow-red-500/20"
              initial={{ scale: 0.5, opacity: 0, rotate: -15 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              data-testid="landing-logo"
            />
            <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tight leading-none mb-4"
                style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}>
              {t('build_your')}
              <br />
              <span className="text-red-600">{t('engine')}</span>
            </h1>
            <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              {t('tagline')}
            </p>
          </motion.div>

          {/* Feature Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-3 gap-4 mb-8"
          >
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.02 }}
                className="text-center"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 border-2 border-red-600 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                  <feature.icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <h3 className="text-white font-bold text-sm md:text-base mb-1">{feature.title}</h3>
                <p className="text-gray-400 text-xs md:text-sm leading-tight">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Decorative dots */}
          <div className="flex justify-center gap-4 mb-6">
            <div className="w-2 h-2 rounded-full bg-gray-500" />
            <div className="w-2 h-2 rounded-full bg-gray-500" />
            <div className="w-2 h-2 rounded-full bg-gray-500" />
          </div>

          {/* Login Card - Metallic Style */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-md mx-auto"
          >
            <Card className="border-0 shadow-2xl overflow-hidden"
                  style={{
                    background: 'linear-gradient(145deg, #d4d4d4 0%, #a3a3a3 50%, #737373 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.2), 0 25px 50px -12px rgba(0,0,0,0.5)'
                  }}>
              {/* Screws decoration */}
              <div className="absolute top-3 left-3 w-3 h-3 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border border-gray-500" />
              <div className="absolute top-3 right-3 w-3 h-3 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border border-gray-500" />
              
              <CardContent className="p-6 pt-8">
                <h2 className="text-2xl font-black text-gray-800 mb-1">
                  {isLogin ? t('welcome_back') : t('join_beta')}
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  {isLogin ? t('tagline').substring(0, 40) + '...' : t('start_training')}
                </p>

                <Tabs value={isLogin ? "login" : "register"} onValueChange={(v) => setIsLogin(v === "login")}>
                  <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-300 p-1 rounded-lg">
                    <TabsTrigger 
                      data-testid="login-tab" 
                      value="login"
                      className="data-[state=active]:bg-gray-700 data-[state=active]:text-white font-bold rounded-md"
                    >
                      {t('login')}
                    </TabsTrigger>
                    <TabsTrigger 
                      data-testid="register-tab" 
                      value="register"
                      className="data-[state=active]:bg-gray-700 data-[state=active]:text-white font-bold rounded-md"
                    >
                      {t('register')}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="login">
                    {sessionExpired && (
                      <div
                        className="mb-4 p-3 border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm rounded"
                        data-testid="session-expired-banner"
                      >
                        Your session expired. Log back in to pick up right where you left off.
                      </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email" className="text-gray-700 font-semibold">{t('email')}</Label>
                        <Input
                          id="login-email"
                          data-testid="login-email-input"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                          className="bg-gray-200 border-gray-400 text-gray-800 placeholder:text-gray-500 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="login-password" className="text-gray-700 font-semibold">{t('password')}</Label>
                          <button
                            type="button"
                            onClick={() => setForgotOpen(true)}
                            className="text-xs font-semibold text-red-700 hover:text-red-600 hover:underline"
                            data-testid="forgot-password-link"
                          >
                            Forgot password?
                          </button>
                        </div>
                        <Input
                          id="login-password"
                          data-testid="login-password-input"
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          required
                          className="bg-gray-200 border-gray-400 text-gray-800 placeholder:text-gray-500 rounded-lg"
                        />
                      </div>
                      <Button
                        data-testid="login-submit-button"
                        type="submit"
                        className="w-full bg-red-700 hover:bg-red-600 text-white font-black uppercase tracking-wide h-12 text-base rounded-lg shadow-lg"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {t('loading')}
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <Play className="w-5 h-5" fill="currentColor" />
                            {t('login').toUpperCase()}
                          </span>
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                  
                  <TabsContent value="register">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-name" className="text-gray-700 font-semibold">{t('full_name')}</Label>
                        <Input
                          id="register-name"
                          data-testid="register-name-input"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your Name"
                          required
                          className="bg-gray-200 border-gray-400 text-gray-800 placeholder:text-gray-500 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-email" className="text-gray-700 font-semibold">{t('email')}</Label>
                        <Input
                          id="register-email"
                          data-testid="register-email-input"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                          className="bg-gray-200 border-gray-400 text-gray-800 placeholder:text-gray-500 rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="register-password" className="text-gray-700 font-semibold">{t('password')}</Label>
                        <Input
                          id="register-password"
                          data-testid="register-password-input"
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          required
                          className="bg-gray-200 border-gray-400 text-gray-800 placeholder:text-gray-500 rounded-lg"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="register-dob" className="text-gray-700 font-semibold">
                          Date of Birth <span className="text-xs font-normal text-gray-600">(13+ required)</span>
                        </Label>
                        <Input
                          id="register-dob"
                          data-testid="register-dob-input"
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth}
                          onChange={handleChange}
                          min={minDob}
                          max={maxDob}
                          required
                          className="bg-gray-200 border-gray-400 text-gray-800 placeholder:text-gray-500 rounded-lg"
                        />
                      </div>
                      
                      {/* Terms and Conditions Checkbox */}
                      <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="terms"
                            data-testid="terms-checkbox"
                            checked={acceptedTerms}
                            onCheckedChange={setAcceptedTerms}
                            className="mt-0.5 border-gray-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                          />
                          <div className="text-sm">
                            <label htmlFor="terms" className="text-gray-700 cursor-pointer">
                              {t('accept_terms')}{" "}
                              <button
                                type="button"
                                onClick={() => setShowTermsModal(true)}
                                className="text-red-600 hover:text-red-500 font-semibold underline"
                              >
                                {t('read_terms')}
                              </button>
                            </label>
                          </div>
                        </div>
                        
                        {/* Mini warning */}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-300">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-xs text-gray-600">
                            {t('health_warning')}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        data-testid="register-submit-button"
                        type="submit"
                        className="w-full bg-red-700 hover:bg-red-600 text-white font-black uppercase tracking-wide h-12 text-base rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading || !acceptedTerms}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {t('loading')}
                          </span>
                        ) : !acceptedTerms ? (
                          t('accept_terms').toUpperCase()
                        ) : (
                          t('start_training').toUpperCase()
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>

          {/* Beta spots indicator — hidden post-launch */}
          {false && betaStatus && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-center mt-6"
            >
              <p className="text-gray-400 text-sm">
                <span className="text-red-500 font-bold">{betaStatus.spots_remaining}</span> {t('beta_spots')}
              </p>
            </motion.div>
          )}

          {/* Footer Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center mt-8 pt-6 border-t border-zinc-800"
          >
            <a 
              href="/privacy" 
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Privacy Policy
            </a>
          </motion.div>
        </div>
      </div>

      <ForgotPasswordModal
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={formData.email}
      />
    </div>
  );
};

export default LoginPage;
